/**
 * Copyright © Veeam Software Group GmbH.
 */

import React from 'react';
import {
    Search,
    TOOLBAR_ITEM_TYPE,
    TOOLBAR_VIEW_TYPE,
    ConfiguredToolbar,
    ConfiguredAdvancedFilter,
    useGlobalLang,
} from '@veeam-vspc/shared/components';
import { ProtectedVirtualMachineRestoreType } from '@veeam-vspc/models/web-controllers';

import type { ConfiguredToolbarProps, ConfiguredAdvancedFilterOptions, AdvancedFilterValue } from '@veeam-vspc/shared/components';
import type { VspcLang } from 'configs/languages';

import backupJobTypeIconSrc from 'images/filters/job-types/backup.png';
import snapshotJobTypeIconSrc from 'images/filters/job-types/snapshot.png';
import remoteSnapshotJobTypeIconSrc from 'images/filters/job-types/remote-snapshot.png';
import archiveJobTypeIconSrc from 'images/filters/job-types/archive.png';
import { useCloudPlatformTypesAdvancedFilter } from 'views/components/filters/CloudPlatformTypesFilters';

export interface PublicCloudsFilterToolbarProps extends Omit<ConfiguredToolbarProps, 'items'> {
}

export const PublicCloudsFilterToolbar: React.FC<PublicCloudsFilterToolbarProps> = (props) => {
    const lang = useGlobalLang<VspcLang>();
    const cloudPlatformTypesAdvancedFilter = useCloudPlatformTypesAdvancedFilter({ propName: 'platformType' });
    const advancedFilterOptions: ConfiguredAdvancedFilterOptions<AdvancedFilterValue> = {
        header: `${lang.FILTER_PROTECTED_WORKLOADS_BY}:`,
        cols: [
            {
                groups: [
                    {
                        title: lang.TYPE,
                        items: [
                            {
                                iconSrc: backupJobTypeIconSrc,
                                label: lang.BACKUP,
                                value: {
                                    id: ProtectedVirtualMachineRestoreType.Backup,
                                    name: 'backupType',
                                },
                            },
                            {
                                iconSrc: snapshotJobTypeIconSrc,
                                label: lang.SNAPSHOT,
                                value: {
                                    id: ProtectedVirtualMachineRestoreType.Snapshot,
                                    name: 'backupType',
                                },
                            },
                            {
                                iconSrc: remoteSnapshotJobTypeIconSrc,
                                label: lang.REPLICA_SNAPSHOT,
                                value: {
                                    id: ProtectedVirtualMachineRestoreType.RemoteSnapshot,
                                    name: 'backupType',
                                },
                            },
                            {
                                iconSrc: archiveJobTypeIconSrc,
                                label: lang.ARCHIVE,
                                value: {
                                    id: ProtectedVirtualMachineRestoreType.PublicCloudArchive,
                                    name: 'backupType',
                                },
                            },
                        ],
                    },
                ],
            },
            {
                groups: [
                    cloudPlatformTypesAdvancedFilter,
                ],
            },
        ],
    };

    return (
        <ConfiguredToolbar
            {...props}
            view={TOOLBAR_VIEW_TYPE.spotted}
            items={[
                {
                    type: TOOLBAR_ITEM_TYPE.customControl,
                    render: () => (
                        <Search
                            name='vmName'
                            placeholder={lang.VM}
                        />
                    ),
                },
                {
                    type: TOOLBAR_ITEM_TYPE.customControl,
                    render: () => (
                        <ConfiguredAdvancedFilter
                            label={lang.FILTER}
                            options={advancedFilterOptions}
                        />
                    ),
                },
            ]}
        />
    );
};
